import { changeNodeDataThunk } from '@/common/reducers/ChatbotThunks';
import { namespace } from '@common/config/constants';
import { useAppDispatch } from '@/common/reducers/hooks/store.hook';
import { closeToolSettingsModal } from '@/common/reducers/ToolSettingsModalReducer';
import { CommonRootState } from '@/common/types/common-root-state-type';
import {
  Button
} from '@/components/ui/button';
import {
  Loader2,
  Wrench,
  X
} from "lucide-react";
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { validateToolSettings } from './tool-settings-validator';
// Define a common interface for all tool settings components
export interface ToolSettingsComponentProps<T = any> {
  initialSettings?: T;
  onSaveSettings?: (settings: any) => void;
  onValidationChange?: (hasErrors: boolean) => void;
}

interface ToolSettingsDialogProps {
  open: boolean;
  toolName: string;
  initialSettings?: any;
  children?: ReactElement<ToolSettingsComponentProps>;
}

const ToolSettingsDialog: React.FC<ToolSettingsDialogProps> = ({
  open,
  toolName,
  initialSettings = {},
  children
}) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [currentSettings, setCurrentSettings] = useState(initialSettings);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const isRTL = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';

  const nodeId = useSelector((state: CommonRootState) => state.ToolSettingsModalReducer.nodeId);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const getLocalizedToolName = (name: string): string => {
    const normalizedName = (name || '').toLowerCase().replace(/[\s_-]/g, '');
    if (normalizedName === 'knowledgebase') {
      return t('chatbot.toolSettings.knowledgeBase.toolName');
    }
    return name;
  };

  const localizedToolName = getLocalizedToolName(toolName);

  // Reset validation errors when dialog opens
  useEffect(() => {
    if (open) {
      console.log(`Tool Settings Dialog opened for: ${toolName}`);
      console.log('Initial settings:', initialSettings);
      setCurrentSettings(initialSettings);
      setHasValidationErrors(false);
      setValidationMessage('');

      // Validate initial settings
      const validation = validateToolSettings(toolName, initialSettings);
      setHasValidationErrors(!validation.isValid);
      if (!validation.isValid) {
        setValidationMessage(validation.message);
      }
    }
  }, [open, toolName, initialSettings]);

  // Add debug logging for validation errors
  useEffect(() => {
    console.log(`Validation errors: ${hasValidationErrors}`);
    if (hasValidationErrors) {
      console.log(`Validation message: ${validationMessage}`);
    }
  }, [hasValidationErrors, validationMessage]);

  const handleSave = () => {
    console.log('Save button clicked. Current settings:', currentSettings);

    // Validate settings before saving
    const validation = validateToolSettings(toolName, currentSettings);
    if (!validation.isValid) {
      setHasValidationErrors(true);
      setValidationMessage(validation.message);
      console.log(`Cannot save: ${validation.message}`);
      return;
    }

    setLoading(true);

    try {
      // Dispatch a custom event to notify the knowledge-base-settings component
      console.log('Dispatching save-tool-settings event');
      document.dispatchEvent(new CustomEvent('save-tool-settings'));

      // Small delay to ensure the event handlers have time to process
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        // Call the onSave callback with the current settings
        console.log(`Calling onSave for tool: ${toolName} with settings:`, currentSettings);

        // The parent component (AgentNode) already handles organizing settings by tool name,
        // so we just pass the settings directly
        dispatch(changeNodeDataThunk({
          id: nodeId ?? undefined,
          dataToPatch: {
            toolSettings: {
              [toolName]: currentSettings
            }
          }
        }));

        dispatch(closeToolSettingsModal());
        
        setLoading(false);
      }, 50);
    } catch (err) {
      console.error('Failed to save settings', err);
      setLoading(false);
    }
  };

  const handleSettingsChange = (settings: any) => {
    console.log('Settings changed in dialog:', settings);
    setCurrentSettings(settings);

    // Validate the new settings
    const validation = validateToolSettings(toolName, settings);
    console.log(`Validation result for ${toolName}:`, validation);
    setHasValidationErrors(!validation.isValid);
    setValidationMessage(validation.message);
  };

  const handleClose = () => {
    console.log('Closing dialog');
    dispatch(closeToolSettingsModal());
  };

  // Only render if open is true
  if (!open) {
    console.log('Dialog is closed, not rendering');
    return null;
  }

  console.log('Rendering dialog for', toolName);

  // Create the modal content
  const modalContent = (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-[1px] z-[9999] flex items-center justify-center">
      <div
        className="bg-[var(--color-surface)] rounded-lg shadow-xl border border-[var(--color-border)] w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col text-[var(--color-text)]"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Dialog Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <Wrench className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold text-[var(--color-text)]">
              {t('chatbot.toolSettings.dialogTitle', { toolName: localizedToolName })}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-full text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Area with Tabs always visible and scrollable content below */}
        <div className="flex flex-col flex-grow overflow-hidden">
          {/* Clone the child component to get the tabs section */}
          {React.cloneElement(children as React.ReactElement<ToolSettingsComponentProps>, {
            initialSettings,
            onSaveSettings: handleSettingsChange,
            onValidationChange: (hasErrors) => {
              console.log(`Validation change from child component: ${hasErrors}`);
              setHasValidationErrors(hasErrors);
            }
          })}
        </div>

        {/* Validation Error Message - Always visible */}
        {hasValidationErrors && validationMessage && (
          <div className="px-6 py-3 bg-amber-50 dark:bg-amber-950/20 border-t border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300 text-sm">
            {validationMessage}
          </div>
        )}

        {/* Dialog Footer */}
        <div className={`flex justify-end ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} px-6 py-4 bg-slate-50 dark:bg-slate-900/40 border-t border-[var(--color-border)]`}>
          <Button
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            disabled={loading || hasValidationErrors}
            onClick={(event) => {
              event.stopPropagation();
              handleSave();
            }}
          >
            {loading ? (
              <>
                <Loader2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                {t('chatbot.toolSettings.saving')}
              </>
            ) : (
              t('chatbot.toolSettings.saveSettings')
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render the modal directly in the document body
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default ToolSettingsDialog; 
